import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Image as ImageIcon, Video, X, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = [
  { id: "success", label: "성공스토리" },
  { id: "date-review", label: "데이트 후기" },
  { id: "event", label: "이벤트" },
];

export default function CoupleBoardWritePage() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const catParam = searchParams.get("category") || "success";

  const [category, setCategory] = useState(catParam);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleMediaUpload = async (files, type) => {
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        const res = await fetch(`${API_URL}/api/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          const url = data.url || data.file_url;
          if (type === "image") setImages((prev) => [...prev, url]);
          else setVideos((prev) => [...prev, url]);
        }
      }
    } catch { /* ignore */ }
    setUploading(false);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim()) return alert("제목과 내용을 입력해주세요.");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/couple-posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category, title, content, images, videos }),
      });
      if (res.ok) {
        const post = await res.json();
        navigate(`/couple-board/${post.id}`);
      }
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-50 pt-20 flex items-center justify-center">
        <div className="text-stone-400">로그인이 필요합니다.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="couple-write-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-stone-500 hover:text-stone-700 mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm">뒤로가기</span>
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
          <div className="p-6 border-b border-stone-100">
            <h1 className="text-xl font-bold text-stone-800">커플 게시판 글쓰기</h1>
          </div>

          <div className="p-6 space-y-5">
            {/* Category */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">카테고리</label>
              <div className="flex gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCategory(c.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      category === c.id ? "bg-burgundy-500 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                    data-testid={`cat-${c.id}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">제목</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20"
                data-testid="title-input"
              />
            </div>

            {/* Content */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">내용</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="내용을 입력하세요"
                rows={10}
                className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 resize-none"
                data-testid="content-input"
              />
            </div>

            {/* Media Upload */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">사진/영상 첨부</label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg text-sm text-stone-600 cursor-pointer transition-colors">
                  <ImageIcon className="w-4 h-4" />사진
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleMediaUpload([...e.target.files], "image")} />
                </label>
                <label className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 rounded-lg text-sm text-stone-600 cursor-pointer transition-colors">
                  <Video className="w-4 h-4" />영상
                  <input type="file" accept="video/*" className="hidden" onChange={(e) => handleMediaUpload([...e.target.files], "video")} />
                </label>
                {uploading && <span className="text-sm text-stone-400 self-center">업로드 중...</span>}
              </div>

              {/* Preview */}
              {(images.length > 0 || videos.length > 0) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {images.map((img, i) => (
                    <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-stone-200">
                      <img src={img.startsWith("http") ? img : `${API_URL}${img}`} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                  {videos.map((vid, i) => (
                    <div key={i} className="relative w-32 h-24 rounded-lg overflow-hidden border border-stone-200 bg-stone-900 flex items-center justify-center">
                      <Video className="w-6 h-6 text-white/60" />
                      <button onClick={() => setVideos((prev) => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t border-stone-100">
              <Button variant="outline" onClick={() => navigate(-1)}>취소</Button>
              <Button onClick={handleSubmit} disabled={submitting || !title.trim() || !content.trim()} className="bg-burgundy-500 hover:bg-burgundy-600 text-white" data-testid="submit-btn">
                <Send className="w-4 h-4 mr-1" />{submitting ? "등록 중..." : "등록하기"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
